/**
 * Ordre EXPLICITE des catégories d'un graphique, partagé par `BarChart` et `PieChart`.
 *
 * Pourquoi ici plutôt que dans chaque graphique : l'ordre métier d'une colonne de texte libre ne se
 * déduit d'aucun tri. Le tri SQL ne trie que sur la valeur — donc alphabétiquement pour une chaîne,
 * ce qui donne « Basse, Haute, Majeure, Moyenne » là où le métier attend
 * « Basse, Moyenne, Haute, Majeure ». Deux graphiques ont le même besoin ; ils partagent donc une
 * implémentation, et non deux tris qui divergeront.
 *
 * ⚠️ **Les valeurs hors liste sont conservées et placées EN FIN**, jamais écartées. C'est la règle
 * la plus importante de ce fichier. Les colonnes concernées (`Statut`, `Priorité` du module Audit)
 * sont du **texte libre** : aucun enum ne garantit que la liste soit exhaustive. Sur le référentiel
 * « Missions d'audit », le workflow « Réactiver » écrit un statut `Réactivé` qui ne figure dans
 * aucune maquette — l'écarter ferait disparaître ces fiches du graphique **sans erreur**, et
 * faussrait tous les pourcentages, calculés par amCharts sur le total des parts affichées.
 * Une liste explicite ordonne donc, elle ne filtre pas.
 */

/** Rang d'une catégorie : sa position dans la liste, sinon après tout le monde. */
const rang = (valeur: unknown, ordre: unknown[]): number => {
  const i = ordre.indexOf(valeur)
  // ⚠️ `indexOf` rend -1 pour l'inconnu : le laisser tel quel trierait les valeurs hors liste
  // AVANT la première valeur attendue, soit exactement l'inverse du comportement voulu.
  return i === -1 ? Number.POSITIVE_INFINITY : i
}

/**
 * Réordonne `data` selon `ordre`, les valeurs hors liste en fin (leur ordre relatif d'origine étant
 * préservé). Rend `data` inchangé si l'ordre est vide — c'est ce qui garantit qu'un graphique qui
 * ne fournit pas d'ordre ne change pas de comportement.
 *
 * @param categoryField clé portant la valeur de catégorie dans chaque ligne (l'`outputUUID` du groupBy)
 */
export const orderCategories = <T extends Record<string, any>>(
  data: T[],
  categoryField: string,
  ordre?: unknown[] | null
): T[] => {
  if (!Array.isArray(data) || !data.length) return data
  if (!Array.isArray(ordre) || !ordre.length || !categoryField) return data

  // Tri STABLE : `Array.prototype.sort` l'est depuis ES2019, donc deux valeurs hors liste gardent
  // leur ordre d'arrivée (celui du SQL) au lieu d'être permutées arbitrairement.
  return [...data].sort((a, b) => rang(a?.[categoryField], ordre) - rang(b?.[categoryField], ordre))
}

/**
 * Complète les catégories ABSENTES de `data` avec une valeur nulle, pour qu'une catégorie sans
 * donnée reste visible à 0 plutôt que de disparaître de l'axe.
 *
 * ⚠️ Les clés de valeur sont **déduites des lignes existantes** : une ligne complétée doit porter
 * les mêmes champs numériques que ses voisines, sinon amCharts n'affiche rien pour elle. Corollaire
 * assumé : quand `data` est **vide**, il n'y a rien d'où déduire ces clés, donc aucun remplissage
 * n'est possible — un graphique sans aucune donnée reste vide, et c'est le bon rendu.
 *
 * N'ajoute jamais une catégorie déjà présente, et ne retire jamais rien.
 */
export const padMissingCategories = <T extends Record<string, any>>(
  data: T[],
  categoryField: string,
  ordre?: unknown[] | null
): T[] => {
  if (!Array.isArray(data) || !data.length) return data
  if (!Array.isArray(ordre) || !ordre.length || !categoryField) return data

  const presentes = new Set(data.map(d => d?.[categoryField]))
  const manquantes = ordre.filter(v => !presentes.has(v))
  if (!manquantes.length) return data

  // Les clés numériques observées sur l'ENSEMBLE des lignes : une série peut être absente d'une
  // ligne donnée (une priorité ne porte que sa propre série), donc se fier à la première ligne
  // laisserait les autres séries sans valeur.
  const clesValeur = new Set<string>()
  for (const ligne of data) {
    for (const [k, v] of Object.entries(ligne ?? {})) {
      if (k !== categoryField && typeof v === "number") clesValeur.add(k)
    }
  }

  const remplies = manquantes.map(v => {
    const ligne: Record<string, any> = { [categoryField]: v }
    for (const k of clesValeur) ligne[k] = 0
    return ligne as T
  })

  return [...data, ...remplies]
}

/** Complète puis ordonne — l'ordre doit s'appliquer AUX lignes ajoutées, donc en dernier. */
export const applyCategoryOrder = <T extends Record<string, any>>(
  data: T[],
  categoryField: string,
  ordre?: unknown[] | null,
  opts?: { padMissing?: boolean }
): T[] => {
  const complet = opts?.padMissing ? padMissingCategories(data, categoryField, ordre) : data
  return orderCategories(complet, categoryField, ordre)
}
